import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalProgresoBorrado from 'akasia-components/licencia/LicenciasVencidasDataBorrado/ModalProgresoBorrado';

export const BorradoProgresoContext = createContext(null);

export const BorradoProgresoProvider = ({ children }) => {
    const { token } = useToken();
    const { showSuccessToast } = useToast();

    const [progresoBorrado, setProgresoBorrado] = useState({
        show: false,
        enProgreso: false,
        completado: false,
        totalEmpresas: 0,
        procesadas: 0,
        exitosas: 0,
        fallidas: 0,
        empresaActual: null,
        empresas: []
    });

    const pollingProgresoRef = useRef(null);
    const idsEnBorradoRef = useRef([]);

    const detenerPollingProgreso = () => {
        if (pollingProgresoRef.current) {
            clearInterval(pollingProgresoRef.current);
            pollingProgresoRef.current = null;
        }
    };

    const consultarProgreso = async (ids = null) => {
        const idsConsultar = ids || idsEnBorradoRef.current || [];
        try {
            const resp = await fetch('/api/Mantenimiento/ConsultarProgresoBorrado', {
                method: 'POST',
                headers: { 
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idsLicencias: idsConsultar })
            });

            if (resp.ok) {
                const data = await resp.json();
                const tableKeys = Object.keys(data || {});
                const kpiTable = (tableKeys.length > 0 && Array.isArray(data[tableKeys[0]])) ? data[tableKeys[0]] : [];
                const detalleTable = (tableKeys.length > 1 && Array.isArray(data[tableKeys[1]])) ? data[tableKeys[1]] : [];

                const kpi = kpiTable.length > 0 ? kpiTable[0] : null;
                const empresas = detalleTable;

                if (kpi && kpi.iTotal > 0) {
                    const total = kpi.iTotal || 0;
                    const procesadas = kpi.iProcesadas || 0;
                    const terminado = Boolean(kpi.bTerminado === 1 || kpi.bTerminado === true);
                    const enProgreso = !terminado;

                    setProgresoBorrado(prev => ({
                        ...prev,
                        show: terminado ? true : prev.show,
                        enProgreso: enProgreso,
                        completado: terminado,
                        totalEmpresas: total,
                        procesadas: procesadas,
                        exitosas: kpi.iExitosas || 0,
                        fallidas: kpi.iFallidas || 0,
                        empresas: empresas.map(e => ({
                            idLicencia: e.idLicencia,
                            uuidEmpresa: e.UUIDEmpresa || e.uuidEmpresa,
                            nombreEmpresa: e.NombreEmpresa || e.nombreEmpresa,
                            servidor: e.Servidor || e.servidor,
                            baseDatos: e.BaseDatos || e.baseDatos,
                            estatus: e.Estatus || e.estatus,
                            motivo: e.Motivo || e.motivo
                        }))
                    }));

                    if (terminado && pollingProgresoRef.current) {
                        detenerPollingProgreso();
                        showSuccessToast('El mantenimiento de borrado ha finalizado.');
                        window.dispatchEvent(new CustomEvent('akasia:mantenimiento:borrado_terminado', { detail: kpi }));
                    }
                }
            }
        } catch (e) {
            console.error('Error al consultar progreso de borrado:', e);
        }
    };

    const iniciarPollingProgreso = (ids = null) => {
        detenerPollingProgreso();
        if (ids && ids.length > 0) {
            idsEnBorradoRef.current = ids;
        }
        pollingProgresoRef.current = setInterval(() => consultarProgreso(idsEnBorradoRef.current), 2500);
        consultarProgreso(idsEnBorradoRef.current);
    };

    const abrirModalProgreso = () => {
        setProgresoBorrado(prev => ({ ...prev, show: true }));
    };

    const minimizarModalProgreso = () => {
        setProgresoBorrado(prev => ({ ...prev, show: false }));
    };

    useEffect(() => {
        return () => {
            detenerPollingProgreso();
        };
    }, []);

    const value = {
        progresoBorrado,
        setProgresoBorrado,
        idsEnBorradoRef,
        iniciarPollingProgreso,
        detenerPollingProgreso,
        consultarProgreso,
        abrirModalProgreso,
        minimizarModalProgreso
    };

    return (
        <BorradoProgresoContext.Provider value={value}>
            {children}
            <ModalProgresoBorrado 
                progresoBorrado={progresoBorrado} 
                setProgresoBorrado={setProgresoBorrado} 
            />
        </BorradoProgresoContext.Provider>
    );
};

BorradoProgresoProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useBorradoProgreso = () => {
    const context = useContext(BorradoProgresoContext);
    if (!context) {
        throw new Error('useBorradoProgreso debe usarse dentro de un BorradoProgresoProvider');
    }
    return context;
};

export default BorradoProgresoProvider;
